import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isUndefined from 'lodash/isUndefined';
import { Structure } from '@eb/eds-structure';
import { CookieNotification } from '@eb/eds-notification';

import { SiteHeader } from '@eb/site-header';
import { SiteFooter } from '@eb/site-footer';
import { setWindowLocation } from '@eb/http';
import gettext from '@eb/gettext';
import {
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
    FOOTER_LINKS_SHAPE_PROP_TYPE,
} from './constants';

export default class SiteStructure extends Component {
    static propTypes = {
        ...Structure.propTypes,
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        globalHeader: PropTypes.node,
        additionalHeaderContent: PropTypes.node,
        footerLinks: FOOTER_LINKS_SHAPE_PROP_TYPE,
        showBrand: PropTypes.bool,
        showMarketingLinks: PropTypes.bool,
        hideSearchBar: PropTypes.bool,
        onSearchFocus: PropTypes.func,
        onCookieHeaderClose: PropTypes.func,
        onCookieHeaderCallAction: PropTypes.func,
        onMouseEnterGlobalHeader: PropTypes.func,
    };

    _getHeader() {
        const {
            env,
            request,
            user,
            globalHeader,
            additionalHeaderContent,
            hideSearchBar,
            onSearchFocus,
            onCookieHeaderCallAction,
            onCookieHeaderClose,
            featureFlags,
            onMouseEnterGlobalHeader,
        } = this.props;
        let header = globalHeader;
        let cookieHeader;

        if (env.isEmbeddedView) {
            return null;
        }

        // NOTE: We need to support retrieving the `user` object via an API
        // request in order to support varnish-cached pages for logged in users.
        // See EB-39986 for more details
        if (isUndefined(header)) {
            header = (
                <SiteHeader
                    env={env}
                    request={request}
                    user={user}
                    onSearchSelect={setWindowLocation}
                    hideSearchBar={hideSearchBar}
                    onSearchFocus={onSearchFocus}
                    featureFlags={featureFlags}
                    onMouseEnterGlobalHeader={onMouseEnterGlobalHeader}
                />
            );
        }

        if (env.showCookieHeader) {
            // TODO: this message should be moved to:
            // /eventbrite_design_system/src/molecules/notification/constants.js
            // and used as default values on /eventbrite_design_system/src/molecules/notification/CookieNotification.js
            const cookieMessage = gettext(`
                Eventbrite, and certain approved third parties, use functional, analytical
                and tracking cookies (or similar technologies) to understand your event preferences
                and provide you with a customized experience. By closing this banner
                or by continuing to use Eventbrite, you agree.`);
            const cookiePolicyLink = '/cookies/';

            cookieHeader = (
                <CookieNotification
                    message={cookieMessage}
                    linkHref={cookiePolicyLink}
                    cookieName="acceptedCookieHeader"
                    onClose={onCookieHeaderClose}
                    onCallAction={onCookieHeaderCallAction}
                />
            );
        }

        if (additionalHeaderContent || cookieHeader) {
            header = (
                <div>
                    {cookieHeader}
                    {header}
                    {additionalHeaderContent}
                </div>
            );
        }

        return header;
    }

    _getFooter() {
        let {
            env,
            user,
            footer,
            footerLinks,
            showBrand,
            showMarketingLinks,
        } = this.props;

        if (env.isEmbeddedView) {
            return null;
        }

        if (isUndefined(footer) && footerLinks) {
            footer = (
                <SiteFooter
                    serverUrl={env.serverUrl}
                    locales={env.locales}
                    localeInfo={env.localeInfo}
                    isUserAuthenticated={user.isAuthenticated}
                    popularSearches={footerLinks.popularSearches}
                    eventsToPlan={footerLinks.eventsToPlan}
                    thirdFooterColumn={footerLinks.thirdFooterColumn}
                    thirdFooterColumnName={footerLinks.thirdFooterColumnName}
                    countries={footerLinks.countries}
                    showBrand={showBrand}
                    showMinimalLinks={footerLinks.showMinimalLinks}
                    showMarketingLinks={showMarketingLinks}
                    helpLinks={env.helpUrls}
                    onLocaleChange={this._handleLocaleChange.bind(this)}
                />
            );
        }

        return footer;
    }

    _handleLocaleChange(locale) {
        const serverUrl = this.props.env.serverUrl;

        setWindowLocation(
            `${serverUrl}/international/change_locale/?locale=${locale}`,
        );
    }

    render() {
        let { ...structurePassThruProps } = this.props;

        if (structurePassThruProps.env.isEmbeddedView) {
            structurePassThruProps = {
                ...structurePassThruProps,
                pageTitle: undefined,
                navItems: undefined,
                sideDrawerDescription: undefined,
            };
        }

        // structurePassThruProps has to be placed last
        // in case of a specific page's need to override
        // header and/or footer
        return (
            <Structure
                header={this._getHeader()}
                footer={this._getFooter()}
                {...structurePassThruProps}
            />
        );
    }
}
